'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';
import {
  ensureUrlChangeEvent,
  URL_CHANGE_EVENT,
} from '@/app/_lib/url-change-event';

/**
 * Read the sticky header height from the CSS variable. Used only for the
 * drift check below — the actual scroll offset comes from CSS via
 * `scroll-margin-top: var(--header-height)`, so we don't compute it here.
 */
function getHeaderHeight(): number {
  return parseInt(
    getComputedStyle(document.documentElement)
      .getPropertyValue('--header-height')
      .trim() || '0',
    10
  );
}

/**
 * Watches the document for layout shifts after a hash navigation and re-pins
 * the target if lazy-loaded content (Curator, async forms, images, …) has
 * pushed it away from where the user expected to land.
 *
 * Strategy:
 *   1. Wait INITIAL_GUARD_MS for the smooth scroll (issued by the browser or
 *      Next.js Link) to play out — corrections during this window would
 *      cancel the slide animation.
 *   2. Then on every reflow (ResizeObserver on <html>), measure the target's
 *      actual viewport position. If it has drifted from the header offset,
 *      correct with an instant scrollIntoView (CSS scroll-margin handles
 *      the offset for us).
 *   3. Stop when the page is quiet for SETTLE_MS, when the user takes over
 *      (tap, wheel, key), or after MAX_WAIT_MS — whichever comes first.
 */
function trackUntilStable(targetId: string): () => void {
  // With prefers-reduced-motion the smooth scroll is collapsed to instant by
  // the browser, so guarding against an in-flight smooth animation is moot —
  // skip the delay so corrections happen immediately.
  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;
  const INITIAL_GUARD_MS = prefersReducedMotion ? 0 : 700;
  const SETTLE_MS = 1500;
  const MAX_WAIT_MS = 10000;
  const DRIFT_TOLERANCE_PX = 2;

  let cancelled = false;
  let smoothGuardActive = true;
  let settleTimerId: ReturnType<typeof setTimeout> | null = null;

  const stop = () => {
    if (cancelled) return;
    cancelled = true;
    resizeObserver.disconnect();
    if (settleTimerId) clearTimeout(settleTimerId);
    clearTimeout(smoothGuardId);
    clearTimeout(maxWaitId);
    window.removeEventListener('pointerdown', stop);
    window.removeEventListener('touchstart', stop);
    window.removeEventListener('wheel', stop);
    window.removeEventListener('keydown', stop);
  };

  const armSettleTimer = () => {
    if (settleTimerId) clearTimeout(settleTimerId);
    settleTimerId = setTimeout(stop, SETTLE_MS);
  };

  const correctIfDrifted = () => {
    if (cancelled || smoothGuardActive) return;
    const target = document.getElementById(targetId);
    if (!target) return;
    const drift = Math.abs(
      target.getBoundingClientRect().top - getHeaderHeight()
    );
    if (drift > DRIFT_TOLERANCE_PX) {
      target.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
  };

  const resizeObserver = new ResizeObserver(() => {
    correctIfDrifted();
    armSettleTimer();
  });
  resizeObserver.observe(document.documentElement);

  ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach((evt) =>
    { window.addEventListener(evt, stop, { once: true, passive: true }); }
  );

  const smoothGuardId = setTimeout(() => {
    smoothGuardActive = false;
    correctIfDrifted();
    armSettleTimer();
  }, INITIAL_GUARD_MS);

  armSettleTimer();
  const maxWaitId = setTimeout(stop, MAX_WAIT_MS);

  return stop;
}

/**
 * Hook that handles scrolling to anchor elements on page load, route change,
 * and same-page hash clicks.
 *
 * Triggers on:
 *   1. Initial mount with hash in URL
 *   2. Route navigation (pathname change)
 *   3. Any URL change including pushState/replaceState (covers Next.js Link
 *      hash navigation, which `hashchange` does not)
 *
 * The smooth scroll itself is not done here — Next.js Link and the browser
 * native hash-jump handle it (with `scroll-margin-top` from CSS giving the
 * correct header offset). This hook only tracks late layout shifts and
 * re-pins the target when needed.
 */
export function useAnchorScroll() {
  const pathname = usePathname();
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    ensureUrlChangeEvent();

    const setup = () => {
      cleanupRef.current?.();
      cleanupRef.current = null;

      const hash = window.location.hash;
      if (!hash) return;

      cleanupRef.current = trackUntilStable(hash.slice(1));
    };

    setup();
    window.addEventListener(URL_CHANGE_EVENT, setup);

    return () => {
      window.removeEventListener(URL_CHANGE_EVENT, setup);
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
  }, [pathname]);
}
